import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Building2,
  BriefcaseBusiness,
  ChevronDown,
  ExternalLink,
  LogOut,
  Plus,
  Search,
  UserRound,
  X,
} from "lucide-react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  STAGES,
  type BoardCard,
  type BoardResponse,
  type CardDetail,
  type CardKind,
  type GlobalTask,
  type Stage,
  type Task,
} from "../shared/contracts";
import { dueTone, sortBoardCards } from "../shared/sorting";
import { api, ApiRequestError } from "./api";
import { Login } from "./Login";

const Relationships = lazy(() => import("./Relationships"));

type CardFilter = "all" | CardKind;

export function App() {
  const [auth, setAuth] = useState<"loading" | "signed-out" | "signed-in">(
    "loading",
  );
  useEffect(() => {
    api
      .session()
      .then((session) =>
        setAuth(session.authenticated ? "signed-in" : "signed-out"),
      )
      .catch(() => setAuth("signed-out"));
  }, []);
  if (auth === "loading") return <BoardSkeleton />;
  if (auth === "signed-out")
    return <Login onAuthenticated={() => setAuth("signed-in")} />;
  return <Board onSignedOut={() => setAuth("signed-out")} />;
}

function Board({ onSignedOut }: { onSignedOut: () => void }) {
  const [relationshipsOpen,setRelationshipsOpen]=useState(new URLSearchParams(window.location.search).get("view")==="relationships");
  const [board, setBoard] = useState<BoardResponse | null>(null);
  const [searchPage, setSearchPage] = useState<BoardResponse["pages"][Stage] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [cardFilter, setCardFilter] = useState<CardFilter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    const id = new URLSearchParams(window.location.search).get("card");
    return id && /^[a-zA-Z0-9:_-]{1,100}$/.test(id) ? id : null;
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<Stage>>(
    new Set(["killed", "archive"]),
  );
  const [tasksOpen, setTasksOpen] = useState(false);
  const [tasksScrollTop, setTasksScrollTop] = useState(0);
  const [returnToTasks, setReturnToTasks] = useState(false);
  const [followUpCardId, setFollowUpCardId] = useState<string | null>(null);
  const [taskEpoch, setTaskEpoch] = useState(0);
  const createDialog = useRef<HTMLDialogElement>(null);
  const syncCursor = useRef<string | null>(null);
  const announcedIds = useRef(new Set<string>());
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const refresh = useCallback(async () => {
    try {
      setError("");
      const next = await api.board();
      syncCursor.current = next.syncCursor;
      setBoard(next);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 401)
        onSignedOut();
      else
        setError(
          caught instanceof Error
            ? caught.message
            : "The board could not be loaded.",
        );
    } finally {
      setLoading(false);
    }
  }, [onSignedOut]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const allCards = useMemo(
    () => board ? Object.values(board.pages).flatMap((page) => page?.cards ?? []) : [],
    [board],
  );
  const loadStage = useCallback(async (stage: Stage) => {
    if (!board) return;
    const current = board.pages[stage];
    if (current && !current.nextCursor) return;
    try {
      const page = await api.stagePage(stage, current?.nextCursor);
      setBoard((previous) => {
        if (!previous) return previous;
        const prior = previous.pages[stage];
        const cards = [...(prior?.cards ?? []), ...page.cards]
          .filter((card, index, list) => list.findIndex((candidate) => candidate.id === card.id) === index);
        return { ...previous, pages: { ...previous.pages, [stage]: { ...page, cards } } };
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The board page could not be loaded.");
    }
  }, [board]);
  const syncChanges = useCallback(async () => {
    const after = syncCursor.current;
    if (!after || document.visibilityState !== "visible") return;
    try {
      const changes = await api.boardChanges(after);
      syncCursor.current = changes.syncCursor;
      const newPotential = changes.cards.filter((card) => card.stage === "potential" && card.createdAt >= after && !announcedIds.current.has(card.id));
      for (const card of newPotential) announcedIds.current.add(card.id);
      if (newPotential.length) setNotice(`${newPotential.length} new ${newPotential.length === 1 ? "card" : "cards"} in Potential`);
      setBoard((previous) => {
        if (!previous) return previous;
        const pages = { ...previous.pages };
        for (const changed of changes.cards) {
          const wasLoaded = Object.values(pages).some((page) => page?.cards.some((card) => card.id === changed.id));
          for (const stage of STAGES.map((item) => item.id)) {
            const page = pages[stage];
            if (page) pages[stage] = { ...page, cards: page.cards.filter((card) => card.id !== changed.id) };
          }
          const destination = pages[changed.stage];
          if (destination && (wasLoaded || changed.stage === "potential")) {
            pages[changed.stage] = { ...destination, cards: sortBoardCards([changed, ...destination.cards]) };
          }
        }
        return { ...previous, pages, stageCounts: changes.stageCounts, syncCursor: changes.syncCursor, serverTime: changes.serverTime };
      });
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 401) onSignedOut();
    }
  }, [onSignedOut]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") void syncChanges(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    const interval = window.setInterval(() => void syncChanges(), 20_000);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
    };
  }, [syncChanges]);
  useEffect(() => {
    const normalized = query.trim();
    if (!normalized) { setSearchPage(null); return; }
    const timeout = window.setTimeout(() => {
      void api.search(normalized).then(setSearchPage).catch((caught) =>
        setError(caught instanceof Error ? caught.message : "Search could not be completed."),
      );
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [query]);
  const loadSearch = useCallback(async () => {
    const normalized = query.trim();
    if (!normalized || !searchPage?.nextCursor) return;
    try {
      const next = await api.search(normalized, searchPage.nextCursor);
      setSearchPage((previous) => previous
        ? {
            ...next,
            cards: [...previous.cards, ...next.cards].filter(
              (card, index, cards) =>
                cards.findIndex((candidate) => candidate.id === card.id) === index,
            ),
          }
        : next,
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "More search results could not be loaded.");
    }
  }, [query, searchPage]);
  const visibleCards = useMemo(() => {
    const cards = query.trim() ? (searchPage?.cards ?? []) : allCards;
    return cardFilter === "all" ? cards : cards.filter((card) => card.kind === cardFilter);
  }, [allCards, cardFilter, query, searchPage]);
  function stageForOver(overId: string): Stage | null {
    return STAGES.some((stage) => stage.id === overId)
      ? (overId as Stage)
      : (allCards.find((card) => card.id === overId)?.stage ?? null);
  }
  async function finishDrag(event: DragEndEvent) {
    const draggedId = String(event.active.id);
    setActiveId(null);
    if (!event.over || !board) return;
    const stage = stageForOver(String(event.over.id));
    if (!stage) return;
    const targetCards = sortBoardCards(
      allCards.filter(
        (card) => card.stage === stage && card.id !== draggedId,
      ),
    );
    try {
      const overId = String(event.over.id);
      await api.moveCard(draggedId, STAGES.some((item) => item.id === overId)
        ? { stage, placement: "start" }
        : { stage, beforeId: targetCards.find((card) => card.id === overId)?.id ?? undefined, placement: targetCards.some((card) => card.id === overId) ? undefined : "start" });
      await refresh();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The card could not be moved.",
      );
    }
  }
  function openCard(id: string, fromTasks = false) {
    setReturnToTasks(fromTasks);
    if (fromTasks) setTasksOpen(false);
    setSelectedId(id);
  }
  function closeCard() {
    setSelectedId(null);
    if (returnToTasks) setTasksOpen(true);
    setReturnToTasks(false);
  }
  if(relationshipsOpen) return <Suspense fallback={<BoardSkeleton />}><Relationships onBack={()=>setRelationshipsOpen(false)} /></Suspense>;
  if (loading) return <BoardSkeleton />;
  if (!board) return <BoardSkeleton />;
  return (
    <main className="app-shell">
      <header className="app-header">
        <div className="app-title">
          <span className="mini-mark" aria-hidden="true">
            J
          </span>
          <strong>Jobs</strong>
        </div>
        <div className="header-actions">
          <button className="tasks-button" onClick={()=>setRelationshipsOpen(true)}>Relationships</button>
          <div className="kind-filter" role="group" aria-label="Filter cards by type">
            {([
              ["all", "All"],
              ["job", "Jobs"],
              ["person", "People"],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={cardFilter === value}
                className={cardFilter === value ? "is-active" : ""}
                onClick={() => setCardFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="search-box">
            <span className="sr-only">Search cards</span>
            <Search />
            <input
              type="search"
              placeholder="Search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <button className="tasks-button" onClick={() => setTasksOpen(true)}>
            Tasks
          </button>
          <button
            className="header-button"
            onClick={() => createDialog.current?.showModal()}
          >
            <Plus /> New
          </button>
          <button
            className="icon-button"
            title="Sign out"
            aria-label="Sign out"
            onClick={() => void api.logout().then(onSignedOut)}
          >
            <LogOut />
          </button>
        </div>
      </header>
      {error && (
        <div className="error-banner" role="alert">
          {error}
          <button onClick={() => setError("")} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}
      {notice && (
        <div className="notice-banner" role="status">
          {notice}
          <button onClick={() => setNotice("")} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={(event: DragStartEvent) =>
          setActiveId(String(event.active.id))
        }
        onDragEnd={(event) => void finishDrag(event)}
        onDragCancel={() => setActiveId(null)}
      >
        <section className="board" aria-label="Jobs board">
          {STAGES.map((stage) => {
            const cards = sortBoardCards(
              visibleCards.filter((card) => card.stage === stage.id),
            );
            const canCollapse = stage.id === "killed" || stage.id === "archive";
            const isCollapsed = !query.trim() && collapsed.has(stage.id);
            return (
              <Column
                key={stage.id}
                stage={stage.id}
                label={stage.label}
                cards={cards}
                total={query.trim() ? cards.length : (board.stageCounts[stage.id] ?? 0)}
                hasMore={query.trim() ? stage.id === "potential" && Boolean(searchPage?.nextCursor) : Boolean(board.pages[stage.id]?.nextCursor)}
                collapsed={isCollapsed}
                canCollapse={canCollapse}
                onToggle={() => {
                  if (collapsed.has(stage.id) && !board.pages[stage.id]) void loadStage(stage.id);
                  setCollapsed((current) => {
                    const next = new Set(current);
                    next.has(stage.id)
                      ? next.delete(stage.id)
                      : next.add(stage.id);
                    return next;
                  });
                }}
                onLoadMore={() => query.trim() ? void loadSearch() : void loadStage(stage.id)}
                onOpen={openCard}
              />
            );
          })}
        </section>
        <DragOverlay
          dropAnimation={{ duration: 150, easing: "cubic-bezier(.2,.8,.2,1)" }}
        >
          {activeId && board ? (
            <CardSurface
              card={allCards.find((card) => card.id === activeId)!}
              dragging
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      <NewCardDialog
        dialogRef={createDialog}
        onCreated={(card, creationNotice) => {
          createDialog.current?.close();
          if (creationNotice) setNotice(creationNotice);
          openCard(card.id);
          void refresh();
        }}
      />
      {selectedId && (
        <CardSheet
          cardId={selectedId}
          refreshKey={taskEpoch}
          onClose={closeCard}
          onChanged={refresh}
          onTaskCompleted={setFollowUpCardId}
        />
      )}
      {tasksOpen && (
        <TasksDrawer
          initialScrollTop={tasksScrollTop}
          refreshKey={taskEpoch}
          onClose={() => setTasksOpen(false)}
          onOpenCard={(id, scrollTop) => {
            setTasksScrollTop(scrollTop);
            openCard(id, true);
          }}
          onTaskCompleted={setFollowUpCardId}
        />
      )}
      {followUpCardId && (
        <FollowUpDialog
          cardId={followUpCardId}
          onClose={() => setFollowUpCardId(null)}
          onCreated={async () => {
            await refresh();
            setTaskEpoch((current) => current + 1);
          }}
        />
      )}
    </main>
  );
}

function Column({
  stage,
  label,
  cards,
  total,
  hasMore,
  collapsed,
  canCollapse,
  onToggle,
  onLoadMore,
  onOpen,
}: {
  stage: Stage;
  label: string;
  cards: BoardCard[];
  total: number;
  hasMore: boolean;
  collapsed: boolean;
  canCollapse: boolean;
  onToggle: () => void;
  onLoadMore: () => void;
  onOpen: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setLoadingMore(true);
      onLoadMore();
      window.setTimeout(() => setLoadingMore(false), 250);
    }, { rootMargin: "240px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);
  if (collapsed)
    return (
      <button className="collapsed-column" onClick={onToggle}>
        <span>{label}</span>
        <small>{total}</small>
      </button>
    );
  return (
    <section
      className={`column ${isOver ? "is-over" : ""}`}
      ref={setNodeRef}
      aria-labelledby={`stage-${stage}`}
    >
      <header className="column-header">
        <div>
          <h2 id={`stage-${stage}`}>{label}</h2>
          <span>{total}</span>
        </div>
        {canCollapse && (
          <button
            className="quiet-icon"
            onClick={onToggle}
            aria-label={`Collapse ${label}`}
          >
            <ChevronDown />
          </button>
        )}
      </header>
      <SortableContext
        items={cards.map((card) => card.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="card-list">
          {cards.map((card) => (
            <SortableCard
              key={card.id}
              card={card}
              onOpen={() => onOpen(card.id)}
            />
          ))}
          {cards.length === 0 && <div className="column-empty">Drop here</div>}
          {hasMore && (
            <div ref={loadMoreRef} className="load-more-wrap">
              <button className="load-more" onClick={onLoadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>
      </SortableContext>
    </section>
  );
}

function SortableCard({
  card,
  onOpen,
}: {
  card: BoardCard;
  onOpen: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? "sortable-card is-dragging" : "sortable-card"}
      {...attributes}
      {...listeners}
      onClick={onOpen}
      onKeyDown={(event) => {
        listeners?.onKeyDown?.(event);
        if (event.key === "Enter") {
          event.preventDefault();
          onOpen();
        }
      }}
      aria-label={`Open ${card.primaryName}`}
    >
      <CardSurface card={card} />
    </div>
  );
}

function CardSurface({
  card,
  dragging = false,
}: {
  card: BoardCard;
  dragging?: boolean;
}) {
  const tone = dueTone(card.earliestTask?.dueOn);
  const KindIcon = card.kind === "person" ? UserRound : BriefcaseBusiness;
  return (
    <article
      className={`job-card due-${tone} ${dragging ? "drag-overlay" : ""}`}
    >
      <KindIcon
        className="card-kind-icon"
        aria-label={card.kind === "person" ? "Person" : "Job"}
      />
      <h3>{card.primaryName}</h3>
      <p
        className={
          card.company === "Company to confirm"
            ? "company placeholder"
            : "company"
        }
      >
        {card.company}
      </p>
      {card.applicationExecution && (
        <p className="application-status" title={card.applicationExecution.note}>
          <a href={card.applicationExecution.jobpilotUrl} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
            JobPilot: {card.applicationExecution.state.replaceAll("_", " ")}
          </a>
        </p>
      )}
      {card.earliestTask && (
        <div className="task-preview">
          <span className="task-dot" />
          <span>{card.earliestTask.title}</span>
          {card.earliestTask.dueOn && (
            <time dateTime={card.earliestTask.dueOn}>
              {formatDue(card.earliestTask.dueOn)}
            </time>
          )}
        </div>
      )}
      <footer className="card-footer">
        <span>
          Added {formatFriendlyDate(card.createdAt)}
          <br />
          Updated {formatFriendlyDate(card.updatedAt)}
        </span>
        {safeUrl(card.sourceUrl) && (
          <a
            className="card-link"
            href={card.sourceUrl!}
            target="_blank"
            rel="noopener noreferrer"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            aria-label={`Open ${card.primaryName}`}
          >
            <ExternalLink />
          </a>
        )}
      </footer>
    </article>
  );
}

function NewCardDialog({
  dialogRef,
  onCreated,
}: {
  dialogRef: React.RefObject<HTMLDialogElement | null>;
  onCreated: (card: CardDetail, notice?: string) => void;
}) {
  const [kind, setKind] = useState<CardKind>("job");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const linkedInCapture = kind === "job" && isLinkedInJobUrl(url);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const created = linkedInCapture
        ? await api.createCardFromLink(url.trim())
        : {
            card: await api.createCard({
              kind,
              primaryName: name,
              company: company || undefined,
              sourceUrl: url || undefined,
              contactEmail: kind === "person" ? email || null : undefined,
            }),
            ingestion: null,
          };
      setName("");
      setCompany("");
      setUrl("");
      setEmail("");
      onCreated(
        created.card,
        created.ingestion?.status === "unavailable"
          ? "Saved the link. LinkedIn details could not be loaded; retry from the card."
          : undefined,
      );
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Card could not be created.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <dialog
      ref={dialogRef}
      className="modal"
      onClick={(event) => {
        if (event.target === dialogRef.current) dialogRef.current?.close();
      }}
    >
      <form onSubmit={submit} className="modal-panel">
        <div className="modal-heading">
          <div>
            <p className="eyebrow">Add to Potential</p>
            <h2>New card</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={() => dialogRef.current?.close()}
            aria-label="Close"
          >
            <X />
          </button>
        </div>
        <fieldset className="segmented">
          <legend className="sr-only">Card type</legend>
          <button
            type="button"
            className={kind === "job" ? "active" : ""}
            onClick={() => setKind("job")}
          >
            Job
          </button>
          <button
            type="button"
            className={kind === "person" ? "active" : ""}
            onClick={() => setKind("person")}
          >
            Person
          </button>
        </fieldset>
        <Field label={kind === "job" ? "Job title" : "Person name"}>
          <input
            autoFocus
            required={kind !== "job" || !linkedInCapture}
            placeholder={linkedInCapture ? "Filled from LinkedIn" : undefined}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Company">
          <input
            placeholder="Company to confirm"
            value={company}
            onChange={(event) => setCompany(event.target.value)}
          />
        </Field>
        <Field label={kind === "job" ? "Listing URL" : "Profile URL"}>
          <input
            type="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
        </Field>
        {kind === "person" && (
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => dialogRef.current?.close()}
          >
            Cancel
          </button>
          <button
            className="primary-button"
            disabled={
              busy ||
              (kind === "person"
                ? !name.trim()
                : !name.trim() && !linkedInCapture)
            }
          >
            {busy
              ? linkedInCapture
                ? "Fetching job details…"
                : "Adding…"
              : linkedInCapture
                ? "Add & fetch details"
                : "Add card"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function CardSheet({
  cardId,
  refreshKey,
  onClose,
  onChanged,
  onTaskCompleted,
}: {
  cardId: string;
  refreshKey: number;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onTaskCompleted: (cardId: string) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [card, setCard] = useState<CardDetail | null>(null);
  const [error, setError] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [refreshingLinkedIn, setRefreshingLinkedIn] = useState(false);
  const [companyJobsLoading, setCompanyJobsLoading] = useState(false);
  const [companyJobsNotice, setCompanyJobsNotice] = useState("");
  const [careerConnections, setCareerConnections] = useState<{id:string;name:string}[]>([]);
  const [editingDescription, setEditingDescription] = useState(false);
  const [movingStage, setMovingStage] = useState<Stage | null>(null);
  const summaryInFlight = useRef<string | null>(null);
  const queuedSummary = useRef<CardDetail | null>(null);
  const lastSuccessfulSummary = useRef<string | null>(null);
  const requestedSummaryVersions = useRef(new Set<string>());
  const latestSavedDescription = useRef<string | null>(null);
  const load = useCallback(async () => {
    try {
      setCard(await api.card(cardId));
      setError("");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Card could not be loaded.",
      );
    }
  }, [cardId]);
  useEffect(() => {
    if (!dialog.current?.open) dialog.current?.showModal();
    void load();
  }, [load, refreshKey]);
  useEffect(() => {
    let active = true;
    void api.relationshipsForCard(cardId).then((result) => {
      if (active) setCareerConnections(Array.isArray(result.connections) ? result.connections : []);
    }).catch(() => { if (active) setCareerConnections([]); });
    return () => { active = false; };
  }, [cardId]);
  const generateSummary = useCallback(async (candidate: CardDetail) => {
    if (
      !candidate.description.trim() ||
      candidate.summaryBullets.length === 3
    ) {
      return;
    }

    const candidateKey = `${candidate.id}:${candidate.description}`;
    if (lastSuccessfulSummary.current === candidateKey) return;
    if (requestedSummaryVersions.current.has(candidateKey)) return;
    if (summaryInFlight.current) {
      if (summaryInFlight.current !== candidateKey) {
        queuedSummary.current = candidate;
      }
      return;
    }

    summaryInFlight.current = candidateKey;
    requestedSummaryVersions.current.add(candidateKey);
    setSummaryLoading(true);
    setSummaryError("");
    try {
      const summarized = await api.summarizeCard(candidate.id);
      setCard((current) => {
        if (
          current?.id !== candidate.id ||
          current.description !== candidate.description ||
          (latestSavedDescription.current !== null &&
            latestSavedDescription.current !== candidate.description)
        ) {
          return current;
        }
        lastSuccessfulSummary.current = candidateKey;
        return summarized;
      });
    } catch (caught) {
      requestedSummaryVersions.current.delete(candidateKey);
      setSummaryError(
        caught instanceof Error
          ? caught.message
          : "Summary could not be generated.",
      );
    } finally {
      if (summaryInFlight.current === candidateKey) {
        summaryInFlight.current = null;
      }
      const nextCandidate = queuedSummary.current;
      queuedSummary.current = null;
      if (
        nextCandidate &&
        `${nextCandidate.id}:${nextCandidate.description}` !== candidateKey
      ) {
        void generateSummary(nextCandidate);
      } else {
        setSummaryLoading(false);
      }
    }
  }, []);
  useEffect(() => {
    if (card && !editingDescription) void generateSummary(card);
  }, [card, editingDescription, generateSummary]);
  async function save(
    input: Partial<
      Pick<
        CardDetail,
        | "kind"
        | "primaryName"
        | "company"
        | "description"
        | "sourceUrl"
        | "contactEmail"
      >
    >,
  ) {
    if (!card) return;
    if (Object.hasOwn(input, "description")) {
      latestSavedDescription.current = input.description ?? "";
    }
    try {
      const updated = await api.updateCard(card.id, input);
      setCard(updated);
      await onChanged();
      setError("");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Changes could not be saved.",
      );
      await load();
    }
  }
  async function move(stage: Stage) {
    if (!card || stage === card.stage) return;
    setMovingStage(stage);
    try {
      setCard(await api.moveCard(card.id, { stage, placement: "start" }));
      await onChanged();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Stage could not be changed.",
      );
    } finally {
      setMovingStage(null);
    }
  }
  async function refreshLinkedIn() {
    if (!card) return;
    setRefreshingLinkedIn(true);
    try {
      const refreshed = await api.refreshLinkedIn(card.id);
      setCard(refreshed.card);
      await onChanged();
      if (refreshed.ingestion.status === "unavailable") {
        setError("LinkedIn details could not be loaded. You can retry later.");
      } else {
        setError("");
      }
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "LinkedIn details could not be refreshed.",
      );
    } finally {
      setRefreshingLinkedIn(false);
    }
  }
  async function openCompanyJobs() {
    if (!card || card.kind !== "person" || !card.company.trim()) return;
    const cachedUrl = card.companyLinkedInUrl
      ? `${card.companyLinkedInUrl}jobs/`
      : null;
    let tab: Window | null = null;
    if (cachedUrl && safeUrl(cachedUrl)) {
      tab = window.open(cachedUrl, "_blank", "noopener,noreferrer");
    } else {
      tab = window.open("about:blank", "_blank", "noopener,noreferrer");
    }
    if (tab) tab.opener = null;
    if (cachedUrl) {
      if (!tab) setError("Your browser blocked the new tab. Please allow pop-ups for Jobs.");
      return;
    }
    setCompanyJobsLoading(true);
    setCompanyJobsNotice("");
    try {
      const result = await api.companyJobsLink(card.id);
      setCard(result.card);
      if (tab && !tab.closed) {
        tab.location.href = result.jobsUrl;
      } else {
        window.open(result.jobsUrl, "_blank", "noopener,noreferrer");
      }
      setCompanyJobsNotice(
        result.status === "fallback"
          ? "Exact company page wasn’t confirmed; showing LinkedIn search results."
          : "",
      );
      await onChanged();
    } catch (caught) {
      if (tab && !tab.closed) tab.close();
      setError(
        caught instanceof Error
          ? caught.message
          : "Company jobs could not be opened.",
      );
    } finally {
      setCompanyJobsLoading(false);
    }
  }
  const nextAction = card ? nextStageAction(card.stage, card.kind) : null;
  return (
    <dialog
      ref={dialog}
      className="sheet-dialog"
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialog.current) dialog.current?.close();
      }}
    >
      <section className="sheet" aria-label="Card details">
        <header className="sheet-header">
          <span className="kind-label">{card?.kind ?? "Card"}</span>
          <button
            className="icon-button"
            onClick={() => dialog.current?.close()}
            aria-label="Close"
          >
            <X />
          </button>
        </header>
        {!card ? (
          <div className="sheet-loading">{error || "Loading card…"}</div>
        ) : (
          <>
            <section className="sheet-identity">
              <div className="identity-copy">
                {card.kind === "job" && (
                  <a className="career-card-link" href={`/?view=relationships&cardId=${encodeURIComponent(card.id)}`}>
                    Find current & former company connections
                  </a>
                )}
                {card.kind === "person" && careerConnections.length === 0 && (
                  <a className="career-card-link" href={`/?view=relationships&q=${encodeURIComponent(card.primaryName)}`}>
                    Find career & relationship history
                  </a>
                )}
                {careerConnections.map((connection) => (
                  <a key={connection.id} className="career-card-link"
                    href={`/?view=relationships&contact=${encodeURIComponent(connection.id)}&tab=career`}>
                    {card.kind === "person" ? "Career & history" : `${connection.name} · Career & history`}
                  </a>
                ))}
                <input
                  className="title-input"
                  aria-label={card.kind === "job" ? "Job title" : "Person name"}
                  value={card.primaryName}
                  onChange={(event) =>
                    setCard({ ...card, primaryName: event.target.value })
                  }
                  onBlur={() =>
                    card.primaryName.trim() &&
                    void save({ primaryName: card.primaryName.trim() })
                  }
                />
                <span className="company-control">
                  <input
                    className="company-input"
                    aria-label="Company"
                    value={card.company}
                    onChange={(event) => {
                      setCompanyJobsNotice("");
                      setCard({ ...card, company: event.target.value });
                    }}
                    onBlur={() =>
                      void save({
                        company: card.company.trim() || "Company to confirm",
                      })
                    }
                  />
                  {card.kind === "person" &&
                    card.company.trim() &&
                    card.company !== "Company to confirm" && (
                      <button
                        type="button"
                        className="company-jobs-button"
                        aria-label={`Open jobs at ${card.company}`}
                        title={`Open jobs at ${card.company}`}
                        onClick={() => void openCompanyJobs()}
                        disabled={companyJobsLoading}
                      >
                        {companyJobsLoading ? (
                          "Finding company jobs…"
                        ) : (
                          <>
                            <Building2 />
                            <ExternalLink />
                          </>
                        )}
                      </button>
                    )}
                </span>
              </div>
              <Field label="Stage">
                <select
                  value={card.stage}
                  onChange={(event) => void move(event.target.value as Stage)}
                >
                  {STAGES.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={card.kind === "job" ? "Listing URL" : "Profile URL"}
              >
                <span className="url-control">
                  <input
                    type="url"
                    value={card.sourceUrl ?? ""}
                    onChange={(event) =>
                      setCard({
                        ...card,
                        sourceUrl: event.target.value || null,
                      })
                    }
                    onBlur={() => void save({ sourceUrl: card.sourceUrl })}
                  />
                  {safeUrl(card.sourceUrl) && (
                    <a
                      href={card.sourceUrl!}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open link"
                    >
                      <ExternalLink />
                    </a>
                  )}
                </span>
              </Field>
              {card.kind === "job" &&
                card.canonicalKey?.startsWith("linkedin:") && (
                  <button
                    className="linkedin-refresh-button"
                    onClick={() => void refreshLinkedIn()}
                    disabled={refreshingLinkedIn}
                  >
                    {refreshingLinkedIn
                      ? "Refreshing…"
                      : "Retry LinkedIn details"}
                  </button>
                )}
              <div className="stage-actions" aria-label="Quick stage actions">
                {nextAction && (
                  <button
                    className="quick-stage-button primary"
                    onClick={() => void move(nextAction.stage)}
                    disabled={movingStage !== null}
                  >
                    {movingStage === nextAction.stage
                      ? "Moving…"
                      : nextAction.label}
                  </button>
                )}
                {card.stage !== "killed" && (
                  <button
                    className="quick-stage-button"
                    onClick={() => void move("killed")}
                    disabled={movingStage !== null}
                  >
                    {movingStage === "killed" ? "Moving…" : "Killed"}
                  </button>
                )}
              </div>
              {card.kind === "person" && (
                <Field label="Email">
                  <input
                    type="email"
                    value={card.contactEmail ?? ""}
                    onChange={(event) =>
                      setCard({
                        ...card,
                        contactEmail: event.target.value || null,
                      })
                    }
                    onBlur={() =>
                      void save({ contactEmail: card.contactEmail })
                    }
                  />
                </Field>
              )}
            </section>
            {companyJobsNotice && (
              <p className="company-jobs-notice" role="status">
                {companyJobsNotice}
              </p>
            )}
            <div className="sheet-panes">
              <section className="detail-section description-pane">
                <section className="summary-section" aria-live="polite">
                  <div className="summary-heading">
                    <h2>At a glance</h2>
                    {summaryLoading && <span>Summarizing…</span>}
                  </div>
                  {card.summaryBullets.length === 3 ? (
                    <ul>
                      {card.summaryBullets.map((bullet, index) => (
                        <li key={`${index}-${bullet}`}>{bullet}</li>
                      ))}
                    </ul>
                  ) : summaryLoading ? (
                    <div className="summary-skeleton">
                      <i />
                      <i />
                      <i />
                    </div>
                  ) : summaryError ? (
                    <div className="summary-error">
                      <span>{summaryError}</span>
                      <button
                        className="text-button"
                        onClick={() => void generateSummary(card)}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <p className="summary-empty">
                      A concise summary will appear here.
                    </p>
                  )}
                </section>
                <h2>Description</h2>
                {editingDescription ? (
                  <textarea
                    className="description-editor"
                    autoFocus
                    value={card.description}
                    onChange={(event) =>
                      setCard({ ...card, description: event.target.value })
                    }
                    onBlur={() => {
                      setEditingDescription(false);
                      void save({ description: card.description });
                    }}
                  />
                ) : (
                  <button
                    className={`description-display ${card.description ? "" : "empty"}`}
                    onClick={() => setEditingDescription(true)}
                  >
                    {card.description || "Add a description…"}
                  </button>
                )}
              </section>
              <Tasks
                card={card}
                setCard={setCard}
                onChanged={onChanged}
                setError={setError}
                onCompleted={onTaskCompleted}
              />
              <ActivityFeed
                card={card}
                onAdded={(activity) => {
                  setCard({
                    ...card,
                    activities: [...card.activities, activity],
                  });
                  void onChanged();
                }}
                setError={setError}
              />
            </div>
            {error && (
              <p className="form-error sheet-error" role="alert">
                {error}
              </p>
            )}
          </>
        )}
      </section>
    </dialog>
  );
}

function Tasks({
  card,
  setCard,
  onChanged,
  setError,
  onCompleted,
}: {
  card: CardDetail;
  setCard: (card: CardDetail) => void;
  onChanged: () => Promise<void>;
  setError: (error: string) => void;
  onCompleted: (cardId: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [dueOn, setDueOn] = useState("");
  async function add(event: FormEvent) {
    event.preventDefault();
    try {
      const task = await api.createTask(card.id, {
        title,
        dueOn: dueOn || null,
      });
      setCard({ ...card, tasks: [...card.tasks, task] });
      setTitle("");
      setDueOn("");
      await onChanged();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Task could not be added.",
      );
    }
  }
  async function change(
    task: Task,
    input: Parameters<typeof api.updateTask>[1],
  ) {
    try {
      const updated = await api.updateTask(task.id, input);
      setCard({
        ...card,
        tasks: card.tasks.map((item) =>
          item.id === updated.id ? updated : item,
        ),
      });
      await onChanged();
      if (input.completed && !task.completedAt) onCompleted(card.id);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Task could not be updated.",
      );
    }
  }
  async function remove(task: Task) {
    try {
      await api.deleteTask(task.id);
      setCard({
        ...card,
        tasks: card.tasks.filter((item) => item.id !== task.id),
      });
      await onChanged();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Task could not be removed.",
      );
    }
  }
  return (
    <section className="detail-section follow-up-pane">
      <h2>Follow-up</h2>
      <div className="task-list">
        {card.tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            onChange={change}
            onDelete={remove}
          />
        ))}
      </div>
      <form className="task-form" onSubmit={add}>
        <input
          placeholder="Add a follow-up"
          required
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
        <input
          type="date"
          aria-label="Due date"
          value={dueOn}
          onChange={(event) => setDueOn(event.target.value)}
        />
        <button className="secondary-button" disabled={!title.trim()}>
          Add
        </button>
      </form>
    </section>
  );
}

function TaskRow({
  task,
  onChange,
  onDelete,
}: {
  task: Task;
  onChange: (task: Task, input: Parameters<typeof api.updateTask>[1]) => void;
  onDelete?: (task: Task) => void;
}) {
  return (
    <div className={`task-row ${task.completedAt ? "completed" : ""}`}>
      <input
        type="checkbox"
        checked={Boolean(task.completedAt)}
        onChange={(event) =>
          void onChange(task, { completed: event.target.checked })
        }
        aria-label={`Complete ${task.title}`}
      />
      <div>
        <strong>{task.title}</strong>
        {task.dueOn && <time>{formatDue(task.dueOn)}</time>}
      </div>
      {onDelete && (
        <button
          className="quiet-icon"
          onClick={() => void onDelete(task)}
          aria-label={`Delete ${task.title}`}
        >
          <X />
        </button>
      )}
    </div>
  );
}

function ActivityFeed({
  card,
  onAdded,
  setError,
}: {
  card: CardDetail;
  onAdded: (activity: CardDetail["activities"][number]) => void;
  setError: (error: string) => void;
}) {
  const [comment, setComment] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      const activity = await api.addComment(card.id, comment);
      onAdded(activity);
      setComment("");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Comment could not be added.",
      );
    }
  }
  return (
    <section className="detail-section activity-section">
      <h2>Activity & notes</h2>
      <form className="comment-form" onSubmit={submit}>
        <textarea
          placeholder="Add a note…"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
        <button className="secondary-button" disabled={!comment.trim()}>
          Comment
        </button>
      </form>
      <ol className="activity-list">
        {[...card.activities].reverse().map((activity) => (
          <li key={activity.id}>
            <span className={`activity-dot event-${activity.eventType}`} />
            <div>
              {activity.metadata.actor === "assistant" && <small>{activity.metadata.assistant === "JobPilot" ? "JobPilot" : "Dot · Jobs Engagement"}</small>}
              <p style={{ whiteSpace: "pre-wrap" }}>{activity.body || activityLabel(activity.eventType)}</p>
              {typeof activity.metadata.connectionId === "string" && (
                <a href={`/connections?contact=${encodeURIComponent(activity.metadata.connectionId)}`} target="_blank" rel="noreferrer">Related contact & history</a>
              )}
              <time dateTime={activity.createdAt}>
                {new Date(activity.createdAt).toLocaleString([], {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </time>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function TasksDrawer({
  initialScrollTop,
  refreshKey,
  onClose,
  onOpenCard,
  onTaskCompleted,
}: {
  initialScrollTop: number;
  refreshKey: number;
  onClose: () => void;
  onOpenCard: (id: string, scrollTop: number) => void;
  onTaskCompleted: (cardId: string) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const [tasks, setTasks] = useState<GlobalTask[]>([]);
  const [completed, setCompleted] = useState<GlobalTask[] | null>(null);
  const [serverTime, setServerTime] = useState(new Date().toISOString());
  const [error, setError] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const response = await api.tasks("open");
      setTasks(response.tasks);
      setServerTime(response.serverTime);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Tasks could not be loaded.",
      );
    }
  }, []);
  useEffect(() => {
    if (!dialog.current?.open) dialog.current?.showModal();
    if (scroll.current) scroll.current.scrollTop = initialScrollTop;
    void load();
  }, [initialScrollTop, load, refreshKey]);
  async function toggle(task: GlobalTask, checked: boolean) {
    try {
      await api.updateTask(task.id, { completed: checked });
      if (checked) {
        setTasks((current) => current.filter((item) => item.id !== task.id));
        onTaskCompleted(task.cardId);
      } else {
        setCompleted(
          (current) => current?.filter((item) => item.id !== task.id) ?? null,
        );
        await load();
      }
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Task could not be updated.",
      );
    }
  }
  async function openArchive() {
    setArchiveOpen((value) => !value);
    if (!completed) {
      try {
        const response = await api.tasks("completed");
        setCompleted(response.tasks);
      } catch (caught) {
        setError(
          caught instanceof Error
            ? caught.message
            : "Completed tasks could not be loaded.",
        );
      }
    }
  }
  const grouped = groupTasks(tasks, serverTime);
  return (
    <dialog
      ref={dialog}
      className="tasks-dialog"
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialog.current) dialog.current?.close();
      }}
    >
      <section className="tasks-drawer" aria-label="All follow-up tasks">
        <header className="sheet-header">
          <div>
            <p className="eyebrow">All records</p>
            <h2>Tasks</h2>
          </div>
          <button
            className="icon-button"
            onClick={() => dialog.current?.close()}
            aria-label="Close"
          >
            <X />
          </button>
        </header>
        <div ref={scroll} className="tasks-scroll">
          {error && <p className="form-error">{error}</p>}
          {(
            ["Overdue", "Today", "Next 7 days", "Later", "No date"] as const
          ).map((label) =>
            grouped[label].length ? (
              <TaskGroup
                key={label}
                label={label}
                tasks={grouped[label]}
                onToggle={toggle}
                onOpenCard={(id) =>
                  onOpenCard(id, scroll.current?.scrollTop ?? 0)
                }
              />
            ) : null,
          )}
          {tasks.length === 0 && (
            <p className="empty-tasks">No open follow-ups.</p>
          )}
          <button
            className="completed-toggle"
            onClick={() => void openArchive()}
          >
            <ChevronDown className={archiveOpen ? "turned" : ""} /> Completed
            archive
          </button>
          {archiveOpen &&
            (completed ? (
              <TaskGroup
                label="Completed"
                tasks={completed}
                onToggle={toggle}
                onOpenCard={(id) =>
                  onOpenCard(id, scroll.current?.scrollTop ?? 0)
                }
              />
            ) : (
              <p className="empty-tasks">Loading archive…</p>
            ))}
        </div>
      </section>
    </dialog>
  );
}

function TaskGroup({
  label,
  tasks,
  onToggle,
  onOpenCard,
}: {
  label: string;
  tasks: GlobalTask[];
  onToggle: (task: GlobalTask, checked: boolean) => void;
  onOpenCard: (id: string) => void;
}) {
  return (
    <section
      className={`global-task-group group-${label.toLowerCase().replaceAll(" ", "-")}`}
    >
      <h3>{label}</h3>
      {tasks.map((task) => (
        <article className="global-task" key={task.id}>
          <TaskRow
            task={task}
            onChange={(item, input) =>
              onToggle(item as GlobalTask, Boolean(input.completed))
            }
          />
          <button
            className="task-card-link"
            onClick={() => onOpenCard(task.card.id)}
            aria-label={`Open ${task.card.primaryName}`}
          >
            <span>{task.card.primaryName}</span>
            <small>
              {task.card.company} ·{" "}
              {STAGES.find((stage) => stage.id === task.card.stage)?.label}
            </small>
          </button>
        </article>
      ))}
    </section>
  );
}

function FollowUpDialog({
  cardId,
  onClose,
  onCreated,
}: {
  cardId: string;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [title, setTitle] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.createTask(cardId, { title, dueOn });
      try {
        await onCreated();
      } catch {
        // The task is already saved; list refresh can recover on the next open.
      }
      dialog.current?.close();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Follow-up could not be added.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      className="modal followup-dialog"
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialog.current) dialog.current?.close();
      }}
    >
      <form className="modal-panel" onSubmit={submit}>
        <div className="modal-heading">
          <div>
            <p className="eyebrow">Task completed</p>
            <h2>Add another follow-up?</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={() => dialog.current?.close()}
            aria-label="Close"
          >
            <X />
          </button>
        </div>
        <Field label="Follow-up">
          <input
            autoFocus
            required
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
        <Field label="Date">
          <input
            type="date"
            required
            value={dueOn}
            onChange={(event) => setDueOn(event.target.value)}
          />
        </Field>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <button
            className="secondary-button"
            type="button"
            onClick={() => dialog.current?.close()}
          >
            Not now
          </button>
          <button
            className="primary-button"
            disabled={!title.trim() || !dueOn || busy}
          >
            Add follow-up
          </button>
        </div>
      </form>
    </dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}
function BoardSkeleton() {
  return (
    <main className="skeleton-shell">
      <header />
      <section>
        {Array.from({ length: 6 }, (_, index) => (
          <div className="skeleton-column" key={index}>
            <i />
            {Array.from({ length: (index % 3) + 2 }, (_, row) => (
              <b key={row} />
            ))}
          </div>
        ))}
      </section>
    </main>
  );
}
function formatDue(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
}
function formatFriendlyDate(value: string) {
  const date = new Date(value);
  return `${date.getDate()} ${date.toLocaleDateString([], { month: "long" })} ${date.getFullYear()}`;
}
function activityLabel(type: string) {
  return type
    .replaceAll("_", " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}
function safeUrl(value: string | null) {
  try {
    const url = value ? new URL(value) : null;
    return url?.protocol === "http:" || url?.protocol === "https:";
  } catch {
    return false;
  }
}
function isLinkedInJobUrl(value: string) {
  try {
    const url = new URL(value.trim());
    if (!url.hostname.toLowerCase().endsWith("linkedin.com")) return false;
    return (
      /\/jobs\/view\/(?:[^/]*-)?\d{6,}(?:\/|$)/i.test(url.pathname) ||
      /^\d{6,}$/.test(
        url.searchParams.get("currentJobId") ??
          url.searchParams.get("jobId") ??
          "",
      )
    );
  } catch {
    return false;
  }
}
function groupTasks(tasks: GlobalTask[], serverTime: string) {
  const groups = {
    Overdue: [] as GlobalTask[],
    Today: [] as GlobalTask[],
    "Next 7 days": [] as GlobalTask[],
    Later: [] as GlobalTask[],
    "No date": [] as GlobalTask[],
  };
  const today = localDateKey(new Date(serverTime));
  const seventh = new Date(`${today}T12:00:00`);
  seventh.setDate(seventh.getDate() + 7);
  const seventhKey = localDateKey(seventh);
  for (const task of tasks) {
    if (!task.dueOn) groups["No date"].push(task);
    else if (task.dueOn < today) groups.Overdue.push(task);
    else if (task.dueOn === today) groups.Today.push(task);
    else if (task.dueOn <= seventhKey) groups["Next 7 days"].push(task);
    else groups.Later.push(task);
  }
  return groups;
}
function localDateKey(date: Date) {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function nextStageAction(
  stage: Stage,
  kind: CardDetail["kind"],
): { stage: Stage; label: string } | null {
  const actions: Partial<Record<Stage, { stage: Stage; label: string }>> = {
    potential: {
      stage: "applied_contacted",
      label: kind === "person" ? "Mark Contacted" : "Mark Applied",
    },
    priority: {
      stage: "applied_contacted",
      label: kind === "person" ? "Mark Contacted" : "Mark Applied",
    },
    applied_contacted: {
      stage: "actively_engaging",
      label: "Move to Actively Engaging",
    },
    actively_engaging: {
      stage: "early_interviews",
      label: "Start Interviews",
    },
    early_interviews: {
      stage: "late_interviews",
      label: "Advance Interviews",
    },
    late_interviews: {
      stage: "negotiations",
      label: "Start Negotiations",
    },
    negotiations: { stage: "secured", label: "Mark Secured" },
    killed: { stage: "archive", label: "Archive" },
  };
  return actions[stage] ?? null;
}
